import { useEffect, useState } from 'react';
import {
	Modal,
	Form,
	Input,
	Button,
	Select,
	InputNumber,
	Row,
	Col,
	Typography,
	notification,
	Space,
	Checkbox,
} from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Material } from '../../interfaces/MaterialInventory.interface';
import { SignatureDefault } from '..';
import { useDispatch, useSelector } from 'react-redux';
import { closeAddMaterialModal } from '../../redux/slices/material';
import { useNavigate } from 'react-router-dom';
import {
	useGetMaterialVendorsQuery,
	useGetMaterialShapesQuery,
	useGetMaterialTypesQuery,
	useAddMaterialMutation,
	useUpdateMaterialMutation,
	useGetMaterialCategoriesQuery,
	useGetMaterialShelvesQuery,
	useGetMaterialSourcesQuery,
} from '../../redux/api/material';
import { useGetBuildingsQuery } from '../../redux/api/location';
import { useGetUsersQuery, UserQueryInterface } from '../../redux/api/users.ts';
import OperatorPinField from '../Operators/OperatorPinField';
import {
	ANGLE,
	CHANNEL,
	MATERIAL_STATUSES,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
	HEX_BAR,
} from '../../config/constants';
import { RootState } from '../../redux/store';
import MaterialLocationsSummaryModal from '../Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal';
import { getEffectiveShapeName } from '../../utils/shapeUtils';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { useIdempotencyKey } from '../../utils/idempotency';

type MaterialWithCost = Material & {
	shipping_cost?: number;
	use_default_cost_per_ci?: boolean;
	material_source?: { id: string; name: string };
	received_by?: string;
};
interface MaterialModalProps {
	material?: MaterialWithCost | null;
	onClose: () => void;
	refetchData: () => void;
	redirectToInventory?: boolean;
}

const MaterialModal = (props: MaterialModalProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const visible = useSelector(
		(state: RootState) => state.material.addMaterialModalVisible
	);

	const [form] = Form.useForm();

	const [addMaterial, addMaterialResult] = useAddMaterialMutation();
	const [updateMaterial, updateMaterialResult] = useUpdateMaterialMutation();
	const [loading, setLoading] = useState(false);
	// Stable key so a retry after a lost response (e.g. proxy timeout) doesn't
	// create a duplicate material. Rotated only after a confirmed success.
	const { getKey, rotate } = useIdempotencyKey();

	const [selectedShape, setSelectedShape] = useState<string | null | undefined>(
		null
	);
	const [selectedCategory, setSelectedCategory] = useState<
		string | null | undefined
	>(null);
	const [signature, setSignature] = useState<string | null>(null);
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const receivedBy = Form.useWatch('received_by', form);

	const vendorsQuery = useGetMaterialVendorsQuery();
	const shapesQuery = useGetMaterialShapesQuery();
	const categoriesQuery = useGetMaterialCategoriesQuery();
	const typesQuery = useGetMaterialTypesQuery(
		{ material_category: selectedCategory || null },
		{ refetchOnMountOrArgChange: true }
	);
	const materialShelvesQuery = useGetMaterialShelvesQuery();
	const materialSourcesQuery = useGetMaterialSourcesQuery();
	const { data: buildings = [], isLoading: buildingsLoading } = useGetBuildingsQuery();
	const defaultBuildingId = buildings.length === 1 ? buildings[0].id : undefined;
	const usersQuery = useGetUsersQuery({
		groups__name: 'Material Receivers',
	});

	const [showLocationSummary, setShowLocationSummary] = useState(false);
	const [locationSummaryData, setLocationSummaryData] = useState<{
		materialInfo: any;
		lengths: any[];
	}>({
		materialInfo: {},
		lengths: [],
	});

	// State to remember cost fields when toggling 'Use Default Cost per Cubic Inch'
	const [previousCosts, setPreviousCosts] = useState<{
		material_cost?: number;
		shipping_cost?: number;
	}>({});

	function onClose() {
		dispatch(closeAddMaterialModal());
		setSelectedShape(null);
		setShowLocationSummary(false);
		setLocationSummaryData({ materialInfo: {}, lengths: [] });
		setSignature(null);
		setPreviousCosts({});
		form.resetFields();
		props.onClose();
	}

	interface IMaterialFormProps {
		vendor: string;
		shape: string;
		type: string;
		width: string;
		height: string;
		outer_diameter: string;
		inner_diameter: string;
		vendor_part_number: string;
		vendor_description: string;
		notes: string;
		wall_thickness: string;
		purchase_order_number?: string;
		lengths?: {
			purchased_length: number;
			current_length: number;
			quantity: number;
			status: string;
		}[];
		material_cost?: number;
		shipping_cost?: number;
		use_default_cost_per_ci?: boolean;
		material_source?: string;
		signature?: string;
		received_by?: string;
		building_id?: string;
	}

	const handleOk = async (values: IMaterialFormProps) => {
		if (!props.material && (!signature || !values.received_by)) {
			return;
		}

		try {
			setLoading(true);

		const payload = {
			vendor: values.vendor,
			shape: values.shape,
			type: values.type,
			width: values.width,
			height: values.height,
			outer_diameter: values.outer_diameter,
			inner_diameter: values.inner_diameter,
			wall_thickness: values.wall_thickness,
			purchase_order_number: values.purchase_order_number,
			vendor_part_number: values.vendor_part_number,
			vendor_description: values.vendor_description,
			notes: values.notes,
			lengths: values.lengths,
			// On update, only send the cost if the user actually changed it, so a
			// stale prefill can't overwrite cost accumulated by a receipt that
			// landed while the modal was open.
			...(props.material &&
			values.material_cost ===
				(props.material.cost != null ? Number(props.material.cost) : undefined)
				? {}
				: { material_cost: values.material_cost }),
			shipping_cost: values.shipping_cost,
			// ERP-received materials derive their cost from the purchase order;
			// never ask the backend to switch them to default-CPI costing.
			...(props.material?.material_order_line_item
				? {}
				: { use_default_cost_per_ci: values.use_default_cost_per_ci }),
			// On update, only send the source when the user changed it (a cleared
			// select sends null so the source is actually removed).
			...(props.material &&
			(values.material_source ?? null) ===
				(props.material.material_source?.id ?? null)
				? {}
				: { material_source: values.material_source ?? null }),
			signature: signature,
			received_by: values.received_by,
			building_id: values.building_id ?? defaultBuildingId ?? null,
		};

			if (props.material) {
				const materialId = props.material.id;
				const shapeId = values.shape;
				await updateMaterial({
					id: materialId,
					values: payload,
				})
					.unwrap()
					.then(() => {
						onClose();
						notification.success({
							message: 'Material Updated Successfully',
						});
						if (props.redirectToInventory) {
							// Navigate first, then refetch to prevent URL from being overwritten
							navigate(`/material/materials?search=${materialId}&shape=${shapeId}`, { replace: true });
						}
						// Use setTimeout to ensure navigation completes before refetch
						setTimeout(() => {
							props.refetchData();
						}, 0);
					});
			} else if (payload) {
				await addMaterial({ values: payload, idempotencyKey: getKey() })
					.unwrap()
					.then((createdMaterial) => {
						const summaryData = {
							materialInfo: {
								type: createdMaterial.type?.display_name || 'N/A',
								width: createdMaterial.width
									? Number(createdMaterial.width)
									: undefined,
								height: createdMaterial.height
									? Number(createdMaterial.height)
									: undefined,
								outer_diameter: createdMaterial.outer_diameter
									? Number(createdMaterial.outer_diameter)
									: undefined,
								inner_diameter: createdMaterial.inner_diameter
									? Number(createdMaterial.inner_diameter)
									: undefined,
								wall_thickness: createdMaterial.wall_thickness
									? Number(createdMaterial.wall_thickness)
									: undefined,
								shape: createdMaterial.shape?.display_name,
							},
							lengths:
								createdMaterial.lengths?.map((length: any) => ({
									id: length.id,
									friendlyId: length.friendly_id,
									location: length.inventory_location?.display_name ?? 'Unknown',
									length: Number(length.current_length),
									isScrapped: false,
									overflowMessage: length.overflow_message || undefined,
								})) || [],
						};

						setLocationSummaryData(summaryData);
						setShowLocationSummary(true);
						setLoading(false);

						// Success — next submit should use a fresh idempotency key.
						rotate();

						notification.success({
							message: 'Material Created Successfully',
							placement: 'topRight',
						});
					});
			}
		} catch (error) {
			setLoading(false);
			notification.error({
				message: 'An error occurred',
				description: getApiErrorMessage(error),
				placement: 'topRight',
			});
		}
	};

	const handleSummaryComplete = () => {
		const createdMaterial = addMaterialResult.data;
		const shapeId = createdMaterial?.shape?.id;
		const materialId = createdMaterial?.id;

		setShowLocationSummary(false);

		dispatch(closeAddMaterialModal());

		form.resetFields();
		setSelectedShape(null);
		setSignature(null);
		setPreviousCosts({});
		setLocationSummaryData({ materialInfo: {}, lengths: [] });

		if (materialId && shapeId) {
			navigate(`/material/materials?search=${materialId}&shape=${shapeId}`, {
				replace: true,
			});
			setTimeout(() => {
				props.refetchData();
			}, 0);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setSubmitAttempted(false);
		onClose();
	};

	// Populate form with existing vendor material data if passed in
	useEffect(() => {
		if (props.material) {
			form.setFieldsValue({
				vendor: props.material.vendor?.id, // Use the ID for Select component
				shape: props.material.shape?.id, // Use the ID for Select component
				category: props.material.type?.material_category?.id, // Use the ID for Select component
				type: props.material.type?.id, // Use the ID for Select component
				width: props.material.width,
				height: props.material.height,
				outer_diameter: props.material.outer_diameter,
				inner_diameter: props.material.inner_diameter,
				wall_thickness: props.material.wall_thickness,
				purchase_order_number: props.material.purchase_order_number,
				vendor_part_number: props.material.vendor_part_number,
				vendor_description: props.material.vendor_description,
				notes: props.material.notes,
				// The API returns the accumulated cost as "cost" (decimal string)
				material_cost:
					props.material.cost != null ? Number(props.material.cost) : undefined,
				shipping_cost: props.material.shipping_cost,
				use_default_cost_per_ci: props.material.use_default_cost_per_ci,
				material_source: props.material.material_source?.id,
				received_by: props.material.received_by,
				building_id: props.material.building_id ?? defaultBuildingId,
			});
			setSelectedShape(getEffectiveShapeName(props.material.shape?.display_name));
			setSelectedCategory(props.material.type?.material_category?.id);
			setSignature(null); // Reset signature when loading material (new signature required)
			setPreviousCosts({}); // Reset previous costs state on load
			setSubmitAttempted(false);
		} else {
			setSignature(null); // Reset if creating new
			setPreviousCosts({});
			setSubmitAttempted(false);
		}
	}, [props.material, form]);

	// Handle shape change
	const handleShapeChange = (value: string) => {
		setSelectedShape(
			getEffectiveShapeName(
				shapesQuery?.data?.find((x) => x.id === value)?.display_name
			)
		);
		form.resetFields([
			'width',
			'height',
			'outer_diameter',
			'inner_diameter',
			'wall_thickness',
		]);
	};

	useEffect(() => {
		if (addMaterialResult.isSuccess || updateMaterialResult.isSuccess) {
			form.resetFields();
		}
	}, [addMaterialResult, updateMaterialResult]);

	useEffect(() => {
		if (visible && !props.material && materialSourcesQuery.data) {
			const defaultSource = materialSourcesQuery.data.find((x) => x.is_default);
			if (defaultSource) {
				const currentSource = form.getFieldValue('material_source');
				if (!currentSource) {
					form.setFieldsValue({ material_source: defaultSource.id });
				}
			}
		}
	}, [visible, props.material, materialSourcesQuery.data, form]);

	return (
		<Modal
			open={visible}
			title={props.material ? 'Update Material' : 'Add Material'}
			maskClosable={false}
			closable={!showLocationSummary}
			width="960px"
			footer={
				<>
					<div className="flex flex-row justify-end gap-1">
						<Button onClick={handleCancel}>Cancel</Button>
						<Button
							type="primary"
							onClick={() => {
								setSubmitAttempted(true);
								form.submit();
							}}
							loading={loading}
							disabled={loading}
						>
							Submit
						</Button>
					</div>
				</>
			}
			onCancel={handleCancel}
			destroyOnClose={true}
			confirmLoading={loading}
			centered={true}
			styles={{
				body: {
					height: '80vh',
					maxHeight: '850px',
					overflowY: 'auto',
					overflowX: 'hidden',
				},
			}}
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleOk}
				initialValues={{
					lengths: [
						{
							purchased_length: null,
							current_length: null,
							quantity: 1,
							status: 'In Stock',
						},
					],
					material_cost: 0,
					shipping_cost: 0,
				}}
			>
				<Row gutter={[8, 16]}>
					<Col span={6}>
						<Form.Item
							name="vendor"
							label="Vendor"
							rules={[{ required: true, message: 'Please select a vendor!' }]}
						>
							<Select
								placeholder="Select a vendor"
								options={vendorsQuery?.data?.map((x) => {
									return { label: x.display_name, value: x.id };
								})}
							/>
						</Form.Item>
					</Col>

					<Col span={6}>
						<Form.Item
							name="shape"
							label="Shape"
							rules={[{ required: true, message: 'Please select a shape!' }]}
						>
							<Select
								placeholder="Select a shape"
								onChange={handleShapeChange}
								options={shapesQuery?.data?.map((x) => {
									return { label: x.display_name, value: x.id };
								})}
							/>
						</Form.Item>
					</Col>

					<Col span={6}>
						<Form.Item
							name="category"
							label="Category"
							rules={[{ required: true, message: 'Please select a category!' }]}
						>
							<Select
								placeholder="Select a category"
								options={categoriesQuery?.data?.map((x) => {
									return { label: x.display_name, value: x.id };
								})}
								value={selectedCategory}
								onChange={(value) => setSelectedCategory(value)}
							/>
						</Form.Item>
					</Col>

					<Col span={6}>
						<Form.Item
							name="type"
							label="Type"
							rules={[{ required: true, message: 'Please select a type!' }]}
						>
							<Select
								placeholder="Select a type"
								showSearch={true}
								optionFilterProp="label"
								options={typesQuery?.data?.map((x) => {
									return { label: x.display_name, value: x.id };
								})}
								disabled={!selectedCategory}
							/>
						</Form.Item>
					</Col>
				</Row>

		{buildings.length > 1 && (
			<Row gutter={[8, 16]}>
				<Col span={8}>
					<Form.Item
						name="building_id"
						label="Building"
						rules={[{ required: !props.material, message: 'Please select a building' }]}
						initialValue={defaultBuildingId}
					>
						<Select
							placeholder="Select building"
							loading={buildingsLoading}
							disabled={!!props.material}
						>
							{buildings.map((b) => (
								<Select.Option key={b.id} value={b.id}>
									{b.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
			</Row>
		)}

				<Row gutter={[8, 16]}>
				{(selectedShape === RECTANGLE_BAR ||
					selectedShape === RECTANGLE_TUBE ||
					selectedShape === PLATE ||
					selectedShape === ANGLE ||
					selectedShape === CHANNEL ||
					selectedShape === HEX_BAR) && (
						<Col span={8}>
							<Form.Item
								name="height"
								label="Height"
									rules={[
										{
											required: true,
											message: 'Please input the height!',
										},
										{
											validator: async (_, value) => {
												if (value !== undefined && value !== null && Number(value) <= 0) {
													return Promise.reject(new Error('Height must be greater than 0'));
												}
											},
										},
									]}
								>
									<InputNumber
										placeholder="Height"
										style={{ width: '100%' }}
										suffix="inches"
									/>
								</Form.Item>
							</Col>
						)}

					{(selectedShape === RECTANGLE_BAR ||
						selectedShape === RECTANGLE_TUBE ||
						selectedShape === PLATE ||
						selectedShape === ANGLE ||
						selectedShape === CHANNEL) && (
							<Col span={8}>
								<Form.Item
									name="width"
									label="Width"
									rules={[
										{
											required: true,
											message: 'Please input the width!',
										},
										{
											validator: async (_, value) => {
												if (value !== undefined && value !== null && Number(value) <= 0) {
													return Promise.reject(new Error('Width must be greater than 0'));
												}
											},
										},
									]}
								>
									<InputNumber
										placeholder="Width"
										style={{ width: '100%' }}
										suffix="inches"
									/>
								</Form.Item>
							</Col>
						)}

					{(selectedShape === RECTANGLE_TUBE ||
						selectedShape === ANGLE ||
						selectedShape === CHANNEL) && (
							<Col span={8}>
								<Form.Item
									name="wall_thickness"
									label="Wall Thickness"
									rules={[
										{
											required: true,
											message: 'Please input the wall thickness!',
										},
										{
											validator: (_, value) => {
												if (value === undefined || value === null) {
													return Promise.resolve();
												}
												if (value > 99.99) {
													return Promise.reject(
														new Error('Value must be 99.99 or less')
													);
												}
												return Promise.resolve();
											},
										},
									]}
								>
									<InputNumber
										placeholder="Wall Thickness"
										style={{ width: '100%' }}
										suffix="inches"
									/>
								</Form.Item>
							</Col>
						)}
				</Row>

				{(selectedShape === ROUND_BAR || selectedShape === ROUND_TUBE) && (
					<Row gutter={[8, 16]}>
						<Col span={8}>
							<Form.Item
								name="outer_diameter"
								label="Outer Diameter"
								rules={[
									{
										required: true,
										message: 'Please input the outer diameter!',
									},
									{
										validator: (_, value) => {
											if (value === undefined || value === null) {
												return Promise.resolve();
											}
											if (value > 99.99) {
												return Promise.reject(
													new Error('Value must be 99.99 or less')
												);
											}
											return Promise.resolve();
										},
									},
								]}
							>
								<InputNumber
									placeholder="Outer Diameter"
									style={{ width: '100%' }}
									suffix="inches"
								/>
							</Form.Item>
						</Col>
						{selectedShape === ROUND_TUBE && (
							<Col span={8}>
								<Form.Item
									name="inner_diameter"
									label="Inner Diameter"
									rules={[
										{
											validator: async (_, value) => {
												const outer_diameter =
													form.getFieldValue('outer_diameter');

												if (value > outer_diameter - 0.125) {
													return Promise.reject(
														new Error(
															'Inner diameter must be at least 0.125 smaller than the outer diameter'
														)
													);
												}
											},
										},
									]}
								>
									<InputNumber
										placeholder="Inner Diameter"
										style={{ width: '100%' }}
										suffix="inches"
									/>
								</Form.Item>
							</Col>
						)}
					</Row>
				)}

				<Row gutter={[8, 0]} align="middle">
					<Col span={4}>
						<Form.Item name="purchase_order_number" label="PO Number">
							<Input placeholder="PO Number" />
						</Form.Item>
					</Col>
					<Col span={5}>
						<Form.Item
							name="material_source"
							label="Material Source"
							rules={[
								// Materials received through ERP purchase orders have no
								// source, so only new manual entries can require one.
								{
									required: !props.material,
									message: 'Material Source is required',
								},
							]}
						>
							<Select
								placeholder="Select a material source"
								options={materialSourcesQuery?.data?.map((x) => {
									return { label: x.name, value: x.id };
								})}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col span={5}>
						<Form.Item
							noStyle
							shouldUpdate={(prevValues, currentValues) =>
								prevValues.use_default_cost_per_ci !==
								currentValues.use_default_cost_per_ci
							}
						>
							{({ getFieldValue }) => (
								<Form.Item
									name="material_cost"
									label="Material Cost"
									rules={[
										{
											// Only creation collects cost; updates keep the
											// stored cost (zero is valid for e.g. remnants).
											required:
												!props.material &&
												!getFieldValue('use_default_cost_per_ci'),
											message: 'Material Cost is required',
										},
										{
											validator: (_, value) => {
												if (
													!props.material &&
													!getFieldValue('use_default_cost_per_ci')
												) {
													if (
														value !== undefined &&
														value !== null &&
														value <= 0
													) {
														return Promise.reject(
															new Error('Cost must be greater than 0')
														);
													}
												}
												return Promise.resolve();
											},
										},
									]}
									validateTrigger={[]}
								>
									<InputNumber<number>
										formatter={(value) => {
											if (value === undefined || value === null) return '';
											return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
										}}
										parser={(value) => {
											if (value === undefined || value === null || value === '')
												return undefined as unknown as number;
											return Number(value.replace(/\$\s?|(,*)/g, ''));
										}}
										precision={2}
										style={{ width: '100%' }}
										min={0}
										placeholder="Material Cost"
									/>
								</Form.Item>
							)}
						</Form.Item>
					</Col>
					<Col span={5}>
						<Form.Item
							noStyle
							shouldUpdate={(prevValues, currentValues) =>
								prevValues.material_cost !== currentValues.material_cost ||
								prevValues.use_default_cost_per_ci !==
								currentValues.use_default_cost_per_ci
							}
						>
							{({ getFieldValue }) => (
								<Form.Item
									name="shipping_cost"
									label="Shipping Cost"
									rules={[{ required: false }]}
								>
									<InputNumber<number>
										formatter={(value) => {
											if (value === undefined || value === null) return '';
											return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
										}}
										parser={(value) => {
											if (value === undefined || value === null || value === '')
												return undefined as unknown as number;
											return Number(value.replace(/\$\s?|(,*)/g, ''));
										}}
										precision={2}
										style={{ width: '100%' }}
										min={0}
										disabled={
											getFieldValue('material_cost') === undefined ||
											getFieldValue('material_cost') === null ||
											getFieldValue('material_cost') === 0
										}
										placeholder="Shipping Cost"
									/>
								</Form.Item>
							)}
						</Form.Item>
					</Col>
					<Col span={5}>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								marginTop: 4,
							}}
						>
							<span>Use Default</span>
							<span>Cost per Cubic Inch</span>
							<Form.Item
								name="use_default_cost_per_ci"
								valuePropName="checked"
								style={{ marginBottom: 0 }}
							>
								<Checkbox
									disabled={!!props.material?.material_order_line_item}
									onChange={(e) => {
										const isChecked = e.target.checked;
										if (isChecked) {
											// Save current values before zeroing out
											setPreviousCosts({
												material_cost: form.getFieldValue('material_cost'),
												shipping_cost: form.getFieldValue('shipping_cost'),
											});
											form.setFieldsValue({
												material_cost: 0,
												shipping_cost: 0,
											});
										} else {
											// Restore previous values
											form.setFieldsValue({
												material_cost: previousCosts.material_cost !== undefined ? previousCosts.material_cost : form.getFieldValue('material_cost'),
												shipping_cost: previousCosts.shipping_cost !== undefined ? previousCosts.shipping_cost : form.getFieldValue('shipping_cost'),
											});
										}
									}}
								/>
							</Form.Item>
						</div>
					</Col>
				</Row>

				{/* Vendor Part Number */}
				<Form.Item name="vendor_part_number" label="Vendor Part Number">
					<Input placeholder="Vendor Part Number" />
				</Form.Item>

				<Form.Item name="vendor_description" label="Vendor Description">
					<Input.TextArea placeholder="Vendor Description" />
				</Form.Item>

				<Form.Item name="notes" label="Notes">
					<Input.TextArea placeholder="Notes" />
				</Form.Item>

				{!props.material ? (
					<>
						<Typography.Title level={5}>Lengths</Typography.Title>
						<Form.List
							name="lengths"
							rules={[
								{
									validator: async (_, items) => {
										if (!items || items.length < 1) {
											return Promise.reject(
												new Error('Please add ad least one material length')
											);
										}
									},
								},
							]}
						>
							{(fields, { add, remove }) => (
								<>
									{fields.map(({ key, name, ...restField }) => (
										<Space
											key={key}
											style={{ display: 'flex', marginBottom: 8 }}
											align="center"
										>
											<Form.Item
												{...restField}
												label="Purchased Length"
												name={[name, 'purchased_length']}
												rules={[
													{
														required: true,
														message: 'Purchased length is required',
													},
													{
														type: 'number',
														max: 240,
														message: 'Must be a valid number',
													},
												]}
											>
												<InputNumber
													min={1}
													max={240}
													step={0.25}
													precision={2}
													placeholder="Enter purchased length"
													style={{ width: '100%' }}
													suffix={'in'}
												/>
											</Form.Item>

											<Form.Item
												{...restField}
												label="Current Length"
												name={[name, 'current_length']}
												rules={[
													{
														required: true,
														message: 'Current length is required',
													},
													{
														type: 'number',
														max: 240,
														message: 'Must be a valid number',
													},
													// Custom validation rule to check current_length <= purchased_length
													({ getFieldValue }) => ({
														validator(_, value) {
															const purchasedLength = getFieldValue([
																'lengths',
																name,
																'purchased_length',
															]);
															if (!value || value <= purchasedLength) {
																return Promise.resolve();
															}
															return Promise.reject(
																new Error(
																	'Current length cannot be greater than purchased length'
																)
															);
														},
													}),
												]}
											>
												<InputNumber
													min={1}
													max={240}
													step={0.25}
													precision={2}
													placeholder="Enter current length"
													style={{ width: '100%' }}
													suffix={'in'}
												/>
											</Form.Item>

											<Form.Item
												{...restField}
												label="Quantity"
												name={[name, 'quantity']}
												rules={[
													{ required: true, message: 'Quantity is required' },
												]}
											>
												<InputNumber
													min={1}
													step={1}
													placeholder="Enter quantity"
													style={{ width: '100%', maxWidth: '150px' }}
												/>
											</Form.Item>
											<Form.Item
												{...restField}
												label="Status"
												name={[name, 'status']}
												rules={[
													{ required: true, message: 'Status is required' },
												]}
											>
												<Select
													showSearch={true}
													popupMatchSelectWidth={true}
													optionFilterProp="label"
													allowClear={true}
													options={MATERIAL_STATUSES
														.filter((x) => x !== 'Awaiting Delivery' && x !== 'Scrapped')
														.map((x) => ({ label: x, value: x }))
													}
													style={{ width: '250px' }}
												/>
											</Form.Item>
											<CloseOutlined onClick={() => remove(name)} />
										</Space>
									))}
									<Form.Item>
										<Button
											type="dashed"
											onClick={() =>
												add({
													purchased_length: null,
													current_length: null,
													quantity: 1,
													status: 'In Stock',
												})
											}
											block
											icon={<PlusOutlined />}
										>
											Add Material Length
										</Button>
									</Form.Item>
								</>
							)}
						</Form.List>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
							<OperatorPinField
								label="Received By"
								userData={usersQuery.data?.results || []}
								expectedGroups={['Material Receivers']}
								value={receivedBy}
								onResolved={(userId) =>
									form.setFieldsValue({ received_by: userId })
								}
								showError={submitAttempted}
							/>
							<Form.Item name="received_by" hidden>
								<InputNumber />
							</Form.Item>
						</div>

						<div style={{ marginTop: '24px' }}>
							<SignatureDefault
								signature={(value: string) => {
									if (value !== '') {
										setSignature(value);
									} else {
										setSignature(null);
									}
								}}
								showSignature={false}
								clearSignaturePad={signature === null}
							/>
							{!signature && submitAttempted && !props.material && (
								<div className="text-red-500" style={{ marginTop: '5px' }}>
									Signature is required
								</div>
							)}
						</div>
					</>
				) : null}
			</Form>
			{showLocationSummary && (
				<MaterialLocationsSummaryModal
					visible={showLocationSummary}
					onComplete={handleSummaryComplete}
					materialInfo={locationSummaryData.materialInfo}
					lengths={locationSummaryData.lengths}
					title="Material Locations Summary"
					source="receiving"
				/>
			)}
		</Modal>
	);
};

export default MaterialModal;
